import type { CSSProperties } from "react"

export const LABEL_COLOR_KEYS = [
  "grey",
  "slate",
  "purple",
  "blue",
  "green",
  "yellow",
  "orange",
  "pink",
  "red",
] as const

export type LabelColorKey = (typeof LABEL_COLOR_KEYS)[number]

export interface LabelColorSpec {
  hex: string
  name: string
  pillBgLight: string
  pillBgDark: string
  textLight: string
  textDark: string
  avatarText: string
}

export const LABEL_COLORS: Record<LabelColorKey, LabelColorSpec> = {
  grey: {
    hex: "#bec2c8",
    name: "Grey",
    pillBgLight: "rgba(190, 194, 200, 0.15)",
    pillBgDark: "rgba(190, 194, 200, 0.15)",
    textLight: "#7b818b",
    textDark: "#bec2c8",
    avatarText: "#1f2329",
  },
  slate: {
    hex: "#95a2b3",
    name: "Slate",
    pillBgLight: "rgba(149, 162, 179, 0.15)",
    pillBgDark: "rgba(149, 162, 179, 0.15)",
    textLight: "#687386",
    textDark: "#95a2b3",
    avatarText: "#ffffff",
  },
  purple: {
    hex: "#5f6ad2",
    name: "Purple",
    pillBgLight: "rgba(95, 106, 210, 0.15)",
    pillBgDark: "rgba(95, 106, 210, 0.15)",
    textLight: "#5f6ad2",
    textDark: "#828fff",
    avatarText: "#ffffff",
  },
  blue: {
    hex: "#24b5ce",
    name: "Blue",
    pillBgLight: "rgba(36, 181, 206, 0.15)",
    pillBgDark: "rgba(36, 181, 206, 0.15)",
    textLight: "#168aa0",
    textDark: "#24b5ce",
    avatarText: "#ffffff",
  },
  green: {
    hex: "#4cb782",
    name: "Green",
    pillBgLight: "rgba(76, 183, 130, 0.15)",
    pillBgDark: "rgba(76, 183, 130, 0.15)",
    textLight: "#2f8d62",
    textDark: "#4cb782",
    avatarText: "#ffffff",
  },
  yellow: {
    hex: "#f1bf00",
    name: "Yellow",
    pillBgLight: "rgba(241, 191, 0, 0.15)",
    pillBgDark: "rgba(241, 191, 0, 0.15)",
    textLight: "#946f00",
    textDark: "#f1bf00",
    avatarText: "#1f2329",
  },
  orange: {
    hex: "#f2994b",
    name: "Orange",
    pillBgLight: "rgba(242, 153, 75, 0.15)",
    pillBgDark: "rgba(242, 153, 75, 0.15)",
    textLight: "#b76018",
    textDark: "#f2994b",
    avatarText: "#1f2329",
  },
  pink: {
    hex: "#f7c8c1",
    name: "Pink",
    pillBgLight: "rgba(247, 200, 193, 0.15)",
    pillBgDark: "rgba(247, 200, 193, 0.15)",
    textLight: "#b06f66",
    textDark: "#f7c8c1",
    avatarText: "#1f2329",
  },
  red: {
    hex: "#eb5758",
    name: "Red",
    pillBgLight: "rgba(235, 87, 88, 0.15)",
    pillBgDark: "rgba(235, 87, 88, 0.15)",
    textLight: "#c83c3d",
    textDark: "#eb5758",
    avatarText: "#ffffff",
  },
}

export function labelPillStyle(
  colorKey: LabelColorKey,
  isDark: boolean,
): CSSProperties {
  const color = LABEL_COLORS[colorKey]

  return {
    backgroundColor: isDark ? color.pillBgDark : color.pillBgLight,
    color: isDark ? color.textDark : color.textLight,
  }
}

export function labelDotStyle(colorKey: LabelColorKey): CSSProperties {
  return { backgroundColor: LABEL_COLORS[colorKey].hex }
}

export function labelAvatarStyle(colorKey: LabelColorKey): CSSProperties {
  const color = LABEL_COLORS[colorKey]

  return {
    backgroundColor: color.hex,
    color: color.avatarText,
  }
}

export function colorKeyFromString(seed: string): LabelColorKey {
  const normalizedSeed = seed.trim().toLowerCase()
  if (!normalizedSeed) return "grey"

  let hash = 0
  for (let index = 0; index < normalizedSeed.length; index += 1) {
    hash = (hash * 31 + normalizedSeed.charCodeAt(index)) >>> 0
  }

  return LABEL_COLOR_KEYS[hash % LABEL_COLOR_KEYS.length]
}

export function workspaceInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)

  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()

  return `${words[0][0]}${words[1][0]}`.toUpperCase()
}
